/** Converte HH:mm em minutos. */
export function printTimeToMinutes(time) {
    if (!time || typeof time !== 'string') {
        return 0;
    }
    const [hoursPart, minutesPart] = time.split(':');
    const hours = parseInt(hoursPart, 10) || 0;
    const minutes = parseInt(minutesPart, 10) || 0;
    return hours * 60 + minutes;
}

export function createCalculatorPart(overrides = {}) {
    return {
        id: overrides.id || `part-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: overrides.name || '',
        time: overrides.time || '01:00',
        grams: overrides.grams ?? 100,
        costPerKilogram: overrides.costPerKilogram ?? '99.90',
    };
}

export function createCalculatorFilament(overrides = {}) {
    return {
        id: overrides.id || `filament-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: overrides.name || '',
        grams: overrides.grams ?? 100,
        costPerKilogram: overrides.costPerKilogram ?? '99.90',
    };
}

/** Quantidade impressa nesta tiragem. Abaixo de 1 conta como 1. */
export function resolveCalculatorQuantity(value) {
    const quantity = parseInt(value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) {
        return 1;
    }
    return quantity;
}

export function sumParts(parts) {
    return parts.reduce(
        (acc, part) => ({
            minutes: acc.minutes + printTimeToMinutes(part.time),
            grams: acc.grams + (parseFloat(part.grams) || 0),
        }),
        { minutes: 0, grams: 0 },
    );
}

function parseNumber(value, fallback = 0) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
}

/** Repassa preço para cobrir taxa percentual sobre o valor final (ex.: marketplace 16%). */
export function grossUpForPercentFee(amount, percent) {
    const fee = parseNumber(percent, 0);
    if (fee <= 0 || fee >= 100) {
        return amount;
    }
    return amount / (1 - fee / 100);
}

/** Valor líquido após descontar taxa percentual do preço bruto. */
export function netAfterPercentFee(grossAmount, percent) {
    const fee = parseNumber(percent, 0);
    return grossAmount * (1 - fee / 100);
}

/**
 * Depreciação proporcional ao tempo de impressão.
 * Ex.: R$ 5.000 / 5.000 h = R$ 1/h; 2 h de job → R$ 2 de depreciação.
 */
export function calculateDepreciation(printerValue, usefulLifeHours, totalMinutes) {
    const value = parseNumber(printerValue, 0);
    const lifeHours = parseNumber(usefulLifeHours, 0);
    if (value <= 0 || lifeHours <= 0 || totalMinutes <= 0) {
        return { depreciation: 0, costPerHour: 0, printHours: 0 };
    }
    const costPerHour = value / lifeHours;
    const printHours = totalMinutes / 60;
    const depreciation = costPerHour * printHours;
    return { depreciation, costPerHour, printHours };
}

function resolveFilamentCost(input) {
    if (input.multipartEnabled && Array.isArray(input.parts) && input.parts.length > 0) {
        const fallbackCost = parseNumber(input.costPerKilogram, 0);
        return input.parts.reduce((acc, part, index) => {
            const grams = parseNumber(part.grams, 0);
            const costPerKilogram = part.costPerKilogram === undefined || part.costPerKilogram === ''
                ? fallbackCost
                : parseNumber(part.costPerKilogram, 0);
            const cost = (costPerKilogram / 1000) * grams;
            const name = String(part.name || '').trim()
                || (input.parts.length > 1 ? `Peça ${index + 1}` : '');
            acc.totalGrams += grams;
            acc.material += cost;
            acc.filamentLines.push({ name, grams, costPerKilogram, cost });
            return acc;
        }, { totalGrams: 0, material: 0, filamentLines: [] });
    }

    const costPerKilogram = parseNumber(input.costPerKilogram, 0);
    const totalGrams = parseNumber(input.usedMaterial, 0);
    const material = (costPerKilogram / 1000) * totalGrams;
    return {
        totalGrams,
        material,
        filamentLines: [{ name: '', grams: totalGrams, costPerKilogram, cost: material }],
    };
}

/**
 * @param {object} input
 * @returns {object} breakdown
 */
export function calculatePriceQuote(input) {
    const consumptionW = parseNumber(input.consumption, 0);
    const energyTariff = parseNumber(input.energyCost, 0);
    const quantity = resolveCalculatorQuantity(input.quantity);
    const packagingUnit = parseNumber(input.packagingCost, 0);
    const other = parseNumber(input.otherCosts, 0);
    const packaging = packagingUnit * quantity;
    const markupPercent = parseNumber(input.markup, 0);
    const marketplacePercent = parseNumber(input.marketplacePercent, 0);
    const cardFeePercent = parseNumber(input.cardFeePercent, 0);
    const pjTaxPercent = parseNumber(input.pjTaxPercent, 0);
    const reworkPercent = parseNumber(input.reworkPercent, 0);
    const salePrice = parseNumber(input.salePrice, 0);

    let totalMinutes = 0;
    if (input.multipartEnabled && Array.isArray(input.parts) && input.parts.length > 0) {
        totalMinutes = sumParts(input.parts).minutes;
    } else {
        totalMinutes = printTimeToMinutes(input.printTime);
    }

    const { totalGrams, material, filamentLines } = resolveFilamentCost(input);
    const kw = consumptionW / 1000;
    const kwh = (kw / 60) * totalMinutes;
    const energy = kwh * energyTariff;
    const { depreciation, costPerHour, printHours } = calculateDepreciation(
        input.printerValue,
        input.printerUsefulLifeHours,
        totalMinutes,
    );
    const printCost = energy + material + other + depreciation;
    const reworkReserve = reworkPercent > 0 ? printCost * (reworkPercent / 100) : 0;
    const productionCostBeforeRework = printCost;
    const productionCost = printCost + reworkReserve + packaging;

    const priceWithMarkup = productionCost * (1 + markupPercent / 100);
    const unitPriceWithMarkup = priceWithMarkup / quantity;
    const unitAfterMarketplace = grossUpForPercentFee(unitPriceWithMarkup, marketplacePercent);
    const unitAfterCard = grossUpForPercentFee(unitAfterMarketplace, cardFeePercent);
    const unitSuggestedListPrice = grossUpForPercentFee(unitAfterCard, pjTaxPercent);
    const suggestedListPrice = unitSuggestedListPrice * quantity;

    const marketplaceFeeUnit = unitSuggestedListPrice * (marketplacePercent / 100);
    const cardFeeUnit = unitSuggestedListPrice * (cardFeePercent / 100);
    const pjTaxFeeUnit = unitSuggestedListPrice * (pjTaxPercent / 100);
    const marketplaceFeeOnList = marketplaceFeeUnit * quantity;
    const cardFeeOnList = cardFeeUnit * quantity;
    const pjTaxFeeOnList = pjTaxFeeUnit * quantity;

    const unitSalePrice = salePrice / quantity;
    let netUnitSale = unitSalePrice;
    netUnitSale = netAfterPercentFee(netUnitSale, marketplacePercent);
    netUnitSale = netAfterPercentFee(netUnitSale, cardFeePercent);
    netUnitSale = netAfterPercentFee(netUnitSale, pjTaxPercent);
    const netFromSale = netUnitSale * quantity;
    const profitOnMarkup = priceWithMarkup - productionCost;
    const marginOnMarkup = priceWithMarkup > 0 ? (profitOnMarkup / priceWithMarkup) * 100 : 0;

    const profitOnSale = netFromSale - productionCost;
    const marginOnSale = salePrice > 0 ? (profitOnSale / salePrice) * 100 : 0;
    const totalFeesOnInformedPrice = salePrice > 0 ? (unitSalePrice - netUnitSale) * quantity : 0;

    let netUnitSuggested = unitSuggestedListPrice;
    netUnitSuggested = netAfterPercentFee(netUnitSuggested, marketplacePercent);
    netUnitSuggested = netAfterPercentFee(netUnitSuggested, cardFeePercent);
    netUnitSuggested = netAfterPercentFee(netUnitSuggested, pjTaxPercent);
    const netFromSuggested = netUnitSuggested * quantity;
    const profitOnSuggested = netFromSuggested - productionCost;
    const marginOnSuggested =
        suggestedListPrice > 0 ? (profitOnSuggested / suggestedListPrice) * 100 : 0;

    return {
        quantity,
        totalMinutes,
        totalGrams,
        filamentLines,
        energy,
        material,
        packagingUnit,
        packaging,
        other,
        depreciation,
        costPerHour,
        printHours,
        productionCostBeforeRework,
        reworkReserve,
        productionCost,
        priceWithMarkup,
        profitOnMarkup,
        marginOnMarkup,
        suggestedListPrice,
        unitSuggestedListPrice,
        marketplaceFeeUnit,
        cardFeeUnit,
        pjTaxFeeUnit,
        marketplaceFeeOnList,
        cardFeeOnList,
        pjTaxFeeOnList,
        salePrice,
        totalFeesOnInformedPrice,
        netFromSale,
        profitOnSuggested,
        marginOnSuggested,
        profitOnSale,
        marginOnSale,
    };
}

export const CALCULATOR_STORAGE_KEYS = {
    productName: 'calculatorProductName',
    quantity: 'calculatorQuantity',
    consumption: 'printerConsumption',
    energyCost: 'energyCost',
    costPerKilogram: 'costPerKilogram',
    filaments: 'calculatorFilaments',
    markup: 'markup',
    packagingCost: 'calculatorPackagingCost',
    marketplacePercent: 'calculatorMarketplacePercent',
    cardFeePercent: 'calculatorCardFeePercent',
    pjTaxPercent: 'calculatorPjTaxPercent',
    multipartEnabled: 'calculatorMultipartEnabled',
    parts: 'calculatorParts',
    printerValue: 'calculatorPrinterValue',
    printerUsefulLifeHours: 'calculatorPrinterUsefulLifeHours',
    reworkPercent: 'calculatorReworkPercent',
};

export function loadCalculatorPreference(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null || raw === '') {
            return fallback;
        }
        return raw;
    } catch {
        return fallback;
    }
}

export function saveCalculatorPreference(key, value) {
    try {
        localStorage.setItem(key, String(value));
    } catch {
        /* ignore */
    }
}

function readStoredFilaments() {
    try {
        const raw = localStorage.getItem(CALCULATOR_STORAGE_KEYS.filaments);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
        return null;
    }
}

export function loadCalculatorParts() {
    const fallbackCost = loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.costPerKilogram, '99.90');
    const storedFilaments = readStoredFilaments();
    try {
        const raw = localStorage.getItem(CALCULATOR_STORAGE_KEYS.parts);
        if (!raw) {
            return [createCalculatorPart({ name: 'Peça 1', costPerKilogram: fallbackCost })];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [createCalculatorPart({ name: 'Peça 1', costPerKilogram: fallbackCost })];
        }
        return parsed.map((part, index) => {
            const filament = storedFilaments?.[index];
            const hasOwnCost = part.costPerKilogram !== undefined
                && part.costPerKilogram !== null
                && String(part.costPerKilogram) !== '';
            return createCalculatorPart({
                ...part,
                grams: hasOwnCost || !filament ? part.grams : filament.grams,
                costPerKilogram: hasOwnCost
                    ? part.costPerKilogram
                    : (filament?.costPerKilogram ?? fallbackCost),
            });
        });
    } catch {
        return [createCalculatorPart({ name: 'Peça 1', costPerKilogram: fallbackCost })];
    }
}

export function saveCalculatorParts(parts) {
    try {
        localStorage.setItem(CALCULATOR_STORAGE_KEYS.parts, JSON.stringify(parts));
    } catch {
        /* ignore */
    }
}

export function loadCalculatorFilaments() {
    try {
        const raw = localStorage.getItem(CALCULATOR_STORAGE_KEYS.filaments);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed.map((item) => createCalculatorFilament(item));
            }
        }
    } catch {
        /* cai no formato antigo, um único custo por kg */
    }

    const cost = loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.costPerKilogram, '99.90');
    const multipart = loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.multipartEnabled, 'false') === 'true';
    if (multipart) {
        const fromParts = loadCalculatorParts().map((part) => createCalculatorFilament({
            name: part.name || '',
            grams: part.grams ?? 0,
            costPerKilogram: cost,
        }));
        if (fromParts.length > 0) {
            return fromParts;
        }
    }
    return [createCalculatorFilament({ grams: 100, costPerKilogram: cost })];
}

export function saveCalculatorFilaments(filaments) {
    try {
        localStorage.setItem(CALCULATOR_STORAGE_KEYS.filaments, JSON.stringify(filaments));
    } catch {
        /* ignore */
    }
}

export function getDefaultCalculatorInputs() {
    const storedFilaments = readStoredFilaments();
    const firstFilament = storedFilaments?.[0];
    return {
        productName: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.productName, ''),
        quantity: resolveCalculatorQuantity(loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.quantity, '1')),
        multipartEnabled: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.multipartEnabled, 'false') === 'true',
        parts: loadCalculatorParts(),
        printTime: '01:00',
        consumption: parseInt(loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.consumption, '350'), 10) || 350,
        printerValue: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.printerValue, '5000'),
        printerUsefulLifeHours: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.printerUsefulLifeHours, '5000'),
        energyCost: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.energyCost, '0.98'),
        costPerKilogram: firstFilament?.costPerKilogram
            ?? loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.costPerKilogram, '99.90'),
        usedMaterial: firstFilament?.grams ?? 100,
        packagingCost: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.packagingCost, '0'),
        otherCosts: 0,
        reworkPercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.reworkPercent, '10'),
        marketplacePercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.marketplacePercent, '0'),
        cardFeePercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.cardFeePercent, '0'),
        pjTaxPercent: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.pjTaxPercent, '0'),
        markup: loadCalculatorPreference(CALCULATOR_STORAGE_KEYS.markup, '100'),
        salePrice: 39.9,
    };
}
